import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import DragIndicatorIcon from "@mui/icons-material/DragIndicatorRounded";
import { Button, Stack, TextField } from "@mui/material";
import { memo } from "react";
import type { DragLayerMonitor } from "react-dnd";
import { useDragLayer } from "react-dnd";
import { makeTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper";
import type { FieldWithSorting } from "../../../../../../hooks/FieldWithOrder";
import SortAscIcon from "@entrilia/shared/icons/SortAscIcon";
import SortDescIcon from "@entrilia/shared/icons/SortDescIcon";
import { DraggingBox } from "../../../dragLayer/DraggingBox";
import type { OptionField } from "../ColumnsOptions.types";

const CustomDragLayerInternal = () => {
  const { isDragging, currentOffset, item } = useDragLayer((monitor: DragLayerMonitor) => {
    return {
      isDragging: monitor.isDragging(),
      currentOffset: monitor.getSourceClientOffset(),
      item: monitor.getItem<{ field: FieldWithSorting; options: OptionField[] }>(),
    };
  });

  return isDragging && currentOffset ? (
    <DraggingBox
      sx={{
        transform: `translate(${currentOffset.x}px, ${currentOffset.y}px)`,
        height: "37px",
        width: "283px",
        backgroundColor: "background.paper",
      }}
    >
      <CustomDragLayerBody field={item.field} options={item.options} />
    </DraggingBox>
  ) : null;
};

const CustomDragLayerBody = memo((props: { field: FieldWithSorting; options: OptionField[] }) => {
  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: 0.7, borderRadius: "4px" }}>
      <DragIndicatorIcon sx={(theme) => ({ color: theme.palette.secondary.light })} />
      <TextField
        value={props.options.find((o) => o.id === props.field.field.name)?.label}
        slotProps={{
          input: {
            endAdornment: <ArrowDropDownRoundedIcon color="secondary" />,
          },
        }}
        sx={{ borderColor: "divider", flex: 1 }}
      />
      <Button
        variant="outlined"
        sx={(theme) => ({
          minWidth: "30px",
          px: "11px",
          py: "7px",
          ".MuiButton-startIcon": {
            mx: "-4px",
          },
          backgroundColor: makeTintedBackgroundFromColor(theme, theme.palette.primary.main),
        })}
        startIcon={props.field.sortAsc ? <SortAscIcon /> : <SortDescIcon />}
      />
    </Stack>
  );
});

CustomDragLayerBody.displayName = "CustomDragLayerBody";

export default memo(CustomDragLayerInternal, () => true);
